const baseURL = process.env.REACT_APP_API_URL;

/**
 * Upload a file to the media service and get back a CDN URL
 * Uses Server-Sent Events (SSE) for progress tracking
 *
 * @param {File} file - The file to upload
 * @param {string} fileType - One of: IMAGE, VIDEO, DOC
 * @param {function} onProgress - Optional callback for progress updates (0-100)
 * @returns {Promise<string>} - The CDN URL of the uploaded file
 */
export const uploadMedia = (file, fileType, onProgress) => {
    return new Promise((resolve, reject) => {
        const token = localStorage.getItem("auth");
        const formData = new FormData();
        formData.append('file', file);

        const xhr = new XMLHttpRequest();

        // Track upload progress
        if (onProgress) {
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percentComplete = Math.round((event.loaded / event.total) * 50); // 0-50% for upload
                    onProgress(percentComplete);
                }
            });
        }

        xhr.open('POST', `${baseURL}/admin/media/upload?fileType=${fileType}`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);

        let cdnUrl = null;
        let buffer = '';

        xhr.onreadystatechange = () => {
            if (xhr.readyState === XMLHttpRequest.LOADING || xhr.readyState === XMLHttpRequest.DONE) {
                // Parse SSE events from the response
                const newData = xhr.responseText.substring(buffer.length);
                buffer = xhr.responseText;

                // Process each line (SSE format: data:{"json":"here"})
                const lines = newData.split('\n');
                for (const line of lines) {
                    if (line.startsWith('data:')) {
                        try {
                            const jsonStr = line.substring(5);
                            const event = JSON.parse(jsonStr);

                            if (event.phase === 'compression' && onProgress) {
                                // 50-100% for compression/processing
                                onProgress(50 + Math.round(event.percent / 2));
                            } else if (event.phase === 'complete') {
                                cdnUrl = event.cdnUrl;
                                if (onProgress) onProgress(100);
                            } else if (event.phase === 'error') {
                                reject(new Error(event.message || 'Upload failed'));
                            }
                        } catch (e) {
                            // Ignore JSON parse errors for incomplete data
                        }
                    }
                }
            }
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                if (cdnUrl) {
                    resolve(cdnUrl);
                } else {
                    reject(new Error('No CDN URL received from server'));
                }
            } else {
                reject(new Error(`Upload failed with status ${xhr.status}`));
            }
        };

        xhr.onerror = () => {
            reject(new Error('Network error during upload'));
        };

        xhr.send(formData);
    });
};

/**
 * Upload a PDF file
 * @param {File} file - The PDF file to upload
 * @param {function} onProgress - Optional callback for progress updates
 * @returns {Promise<string>} - The CDN URL of the uploaded PDF
 */
export const uploadPdf = (file, onProgress) => {
    return uploadMedia(file, 'DOC', onProgress);
};

/**
 * Upload an image file
 * @param {File} file - The image file to upload
 * @param {function} onProgress - Optional callback for progress updates
 * @returns {Promise<string>} - The CDN URL of the uploaded image
 */
export const uploadImage = (file, onProgress) => {
    return uploadMedia(file, 'IMAGE', onProgress);
};
